import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Text,
  TextTag,
  ThemeProvider,
  TokenTextAppearance,
  TokenTextColor,
  Video,
} from '@volkswagen-onehub/components-core';
import {
  getGiveawayAssets,
  GiveawayResponse,
  TaskStatus,
} from '@org-avp/avp-hck-dga-customerui-commons';

import { VideoTracker } from '@/components/cameraflight/videoTracker';
import WebStreamingOverlay from '@/components/streaming/WebStreamingOverlay';
import useBreakpoint from '@/hooks/useBreakpoint';
import { GiveawayContext } from '@/state/state';

import { AnimatedFade } from './AnimatedFade';
import {
  ArrowMaskContainer,
  Styled3DStreamButtonWrapper,
  StyledContentWrapper,
  StyledDisclaimerWrapper,
  StyledFooter,
  StyledFooterContentWrapper,
  StyledHeadingWrapper,
  StyledMediaWrapper,
  StyledOverflowWrapper,
  StyledScrollDownIcon,
  StyledScrollDownText,
  StyledScrollPromptWrapper,
  StyledSection,
  StyledStageImage,
  StyledStageVideoWrapper,
} from './styled.components';

const CAMERA_FLIGHT_POSTER_URL =
  'https://volkswagen.dga.avp.tech/assets/camera_flight_placeholder_v.png';

const CAMERA_FLIGHT_UNAVAILABLE_STATUSES: TaskStatus[] = [TaskStatus.EXPIRED, TaskStatus.ABORTED];

type StageGiveawayData = GiveawayResponse & {
  cameraFlightVideoVertical?: string | null;
};

type Props = {
  disclaimers?: React.ReactNode;
  heading?: React.ReactElement;
  media?: React.ReactElement;
  authorHeight?: string;
  isMediaLoaded?: boolean;
  shouldShow3DStream?: boolean;
};

const toValidAsset = (asset?: string | null): string | undefined => {
  const trimmedAsset = asset?.trim();

  return trimmedAsset ? trimmedAsset : undefined;
};

const firstAsset = (assets?: string[]): string | undefined => {
  return toValidAsset(assets?.[0]);
};

const resolveResponsiveAsset = ({
  shouldUseVerticalMedia,
  horizontal,
  vertical,
  fallbackToVerticalOnHorizontal = true,
}: {
  shouldUseVerticalMedia: boolean;
  horizontal?: string | null;
  vertical?: string | null;
  fallbackToVerticalOnHorizontal?: boolean;
}): string | undefined => {
  const validHorizontal = toValidAsset(horizontal);
  const validVertical = toValidAsset(vertical);

  if (shouldUseVerticalMedia) {
    return validVertical ?? validHorizontal;
  }

  return validHorizontal ?? (fallbackToVerticalOnHorizontal ? validVertical : undefined);
};

export const FullScreenStage = ({
  authorHeight,
  disclaimers,
  heading,
  media,
  isMediaLoaded = true,
  shouldShow3DStream = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation();
  const scrollDownText = t('fullscreenStage.scrollDown');

  const { isMobile, isTablet, isSmallDesktop } = useBreakpoint();
  const giveawayData = useContext(GiveawayContext) as StageGiveawayData;

  const shouldUseVerticalMedia = isMobile || isTablet || isSmallDesktop;

  const cameraFlightFinished = giveawayData.giveawayStatus === TaskStatus.FINISHED;

  const cameraFlightUnavailable = CAMERA_FLIGHT_UNAVAILABLE_STATUSES.includes(
    giveawayData.giveawayStatus,
  );

  const horizontalBeautyShot = firstAsset(getGiveawayAssets(giveawayData, 'beauty-shot'));

  const verticalBeautyShot = firstAsset(getGiveawayAssets(giveawayData, 'beauty-shot-vertical'));

  const selectedBeautyShot = resolveResponsiveAsset({
    shouldUseVerticalMedia,
    horizontal: horizontalBeautyShot,
    vertical: verticalBeautyShot,
  });

  const selectedCameraFlightVideo = resolveResponsiveAsset({
    shouldUseVerticalMedia,
    horizontal: giveawayData.cameraFlightVideo,
    vertical: giveawayData.cameraFlightVideoVertical,
    fallbackToVerticalOnHorizontal: false,
  });

  const shouldShowCameraFlightVideo =
    giveawayData.renderCameraFlight && cameraFlightFinished && Boolean(selectedCameraFlightVideo);

  const shouldShowCameraFlightDisclaimerText =
    giveawayData.renderCameraFlight && !cameraFlightUnavailable && !shouldShowCameraFlightVideo;

  const headingAppearance = isMobile
    ? TokenTextAppearance.headline400
    : TokenTextAppearance.headline600;

  const resolvedHeading = heading ?? (
    <Text bold appearance={headingAppearance}>
      {giveawayData.isCertifiedPreOwned ? t('header.cpoGreetings') : t('header.greetings')}
    </Text>
  );

  const resolvedMedia =
    media ??
    (shouldShowCameraFlightVideo && selectedCameraFlightVideo ? (
      <StyledStageVideoWrapper>
        <Video
          src={selectedCameraFlightVideo}
          data-testid="fullscreen-stage-video"
          tabIndex={-1}
          loop
          autoPlay
          muted
          playsInline
          poster={selectedBeautyShot ?? CAMERA_FLIGHT_POSTER_URL}
          rounded="none"
        >
          <VideoTracker />
        </Video>
      </StyledStageVideoWrapper>
    ) : (
      <>
        {selectedBeautyShot && (
          <StyledStageImage
            src={selectedBeautyShot}
            alt={t('body.details.beautyShot.title')}
            data-testid="fullscreen-stage-image"
          />
        )}
      </>
    ));

  const certifiedPreOwnedDisclaimer =
    disclaimers !== undefined ? (
      disclaimers
    ) : giveawayData.isCertifiedPreOwned ? (
      <div>
        <Text
          tag={TextTag.span}
          appearance={TokenTextAppearance.label100}
          color={TokenTextColor.inherit}
          bold
        >
          {t('legalNotice.heading')}
        </Text>{' '}
        <Text
          tag={TextTag.span}
          appearance={TokenTextAppearance.label100}
          color={TokenTextColor.inherit}
        >
          {t('legalNotice.description')}
        </Text>
      </div>
    ) : undefined;

  const cameraFlightPlaceholderDisclaimer = shouldShowCameraFlightDisclaimerText ? (
    <div>
      <Text
        tag={TextTag.span}
        appearance={TokenTextAppearance.label100}
        color={TokenTextColor.inherit}
        data-testid="fullscreen-stage-camera-flight-placeholder-text"
      >
        {t('giveaway.cameraFlightPlaceholder')}
      </Text>
    </div>
  ) : undefined;

  const resolvedDisclaimers =
    certifiedPreOwnedDisclaimer || cameraFlightPlaceholderDisclaimer ? (
      <>
        {certifiedPreOwnedDisclaimer}
        {cameraFlightPlaceholderDisclaimer}
      </>
    ) : undefined;

  return (
    <ThemeProvider theme="inverted">
      <StyledSection authorHeight={authorHeight}>
        <StyledMediaWrapper>{resolvedMedia}</StyledMediaWrapper>

        <StyledOverflowWrapper isAuthorMode={Boolean(authorHeight)}>
          <StyledContentWrapper>
            <StyledHeadingWrapper startAnimation={isMediaLoaded}>
              {isMediaLoaded ? (
                <AnimatedFade startAnimation={isMediaLoaded}>{resolvedHeading}</AnimatedFade>
              ) : (
                resolvedHeading
              )}
            </StyledHeadingWrapper>
          </StyledContentWrapper>

          <StyledFooter>
            <StyledFooterContentWrapper>
              {resolvedDisclaimers && (
                <StyledDisclaimerWrapper>{resolvedDisclaimers}</StyledDisclaimerWrapper>
              )}

              {shouldShow3DStream && (
                <Styled3DStreamButtonWrapper>
                  {isMediaLoaded ? (
                    <AnimatedFade delay={400} startAnimation={isMediaLoaded}>
                      <WebStreamingOverlay shouldGuardMobileLandscape />
                    </AnimatedFade>
                  ) : (
                    <WebStreamingOverlay shouldGuardMobileLandscape />
                  )}
                </Styled3DStreamButtonWrapper>
              )}
            </StyledFooterContentWrapper>

            <StyledScrollPromptWrapper>
              <StyledScrollDownText>{scrollDownText}</StyledScrollDownText>

              <ArrowMaskContainer>
                <StyledScrollDownIcon startAnimation={isMediaLoaded} aria-label={scrollDownText} />
              </ArrowMaskContainer>
            </StyledScrollPromptWrapper>
          </StyledFooter>
        </StyledOverflowWrapper>
      </StyledSection>
    </ThemeProvider>
  );
};
